import { createConfigParametersRepository } from '../../configParametersRepository';
import { identifyAnalyticsClient } from '../utils/analytics-identification';
import { CustomDimensions } from '../types';
import { get } from '../../utils/httpRequest';
import {
  CUSTOM_DIMENSIONS_STORAGE_KEY,
  CUSTOM_PARAMS_STORAGE_KEY,
  MKTTRACK_COOKIE_NAME,
} from '../resources/constants';
import { getPageLocation } from '../google-analytics/pages/utils';
import { createFirebaseAdapter } from '../firebase/firebaseAdapter';
import { createFirebaseClient } from '../firebase/firebaseClient';
import { extractDomain } from '../utils/url';
import { parseJson } from '../utils/utils';
import { BundleId, FieldsObject, TrackPageOptions } from './types';
import { gaWrapper, gtagWrapper } from '../google-analytics/common';
import { createRuntimeGoogleState } from '../init/ga/state';
import { getBuyPathParam } from '../init/ga/customDimensions';
import { trackEvent } from './events';
import { checkGaInit } from '../init/ga';
import { CustomParams } from '../google-analytics/ga4Types';
import { getCustomDimensionsFromParams } from '../google-analytics';
import { handlePageError } from './error';

const createNativeTracking = (brand: string, virtualPage: string) => {
  const appName =
    {
      ED: 'EDREAMS',
      GV: 'GOV', // legacy
      GO: 'GOV', // new
      OP: 'OPODO',
      TL: 'TRAVELLINK', // legacy
      TR: 'TRAVELLINK', // new
    }[brand] || '';
  return ['send', 'screenview', { screenName: virtualPage, appName: appName }];
};

const createWebTracking = (virtualPage: string) => {
  return [
    'send',
    'pageview',
    virtualPage,
    {
      hitCallback: identifyAnalyticsClient,
    },
  ];
};

const trackPageInUniversal = (args: any[]) => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();

  const storedCustomDimensions = storageClient?.getTrackingStoredValue(
    CUSTOM_DIMENSIONS_STORAGE_KEY
  );

  gaWrapper('set', getCustomDimensionsFromParams(storedCustomDimensions));
  gaWrapper(...args);
};

export const trackPageInGa4 = (screen_name: string) => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();
  const firebaseAdapter = createFirebaseAdapter();
  const firebaseClient = createFirebaseClient(firebaseAdapter);

  const storedCustomParams = storageClient?.getTrackingStoredValue(
    CUSTOM_PARAMS_STORAGE_KEY
  );

  firebaseClient.trackScreenView({
    ...storedCustomParams,
    screen_name,
  });

  const page_location = getPageLocation(screen_name);
  gtagWrapper('event', 'page_view', {
    page_location,
    ...storedCustomParams,
  });
  gtagWrapper('event', 'edo_screen_view', {
    page_location,
    screen_name,
    ...storedCustomParams,
  });
};

const updatePageCustomDimensions = async (params: CustomParams) => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();

  const filteredDimensions = Object.fromEntries(
    Object.entries(params).filter(([_, value]) => value || value === '')
  );

  storageClient?.updateCustomParamsAndDimensions(filteredDimensions);
};

export const getPageCustomDimensions = async (page: string) => {
  const { getGoogleAnalyticsParameters } = createConfigParametersRepository();
  const { endpoint } = getGoogleAnalyticsParameters();
  if (!endpoint.pageCustomDimensions) return {};
  const url = `${endpoint.pageCustomDimensions}${encodeURIComponent(page)}`;
  try {
    const response: CustomDimensions = await get(url, {
      accept: 'application/json, text/javascript, */*; q=0.01',
    });
    return response;
  } catch (_) {
    return {};
  }
};

const getSearchInfoCustomDimensions = async () => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();

  const analyticsSearchInfo: string = await storageClient?.getFromStorageAsync(
    'sessionStorage',
    'analyticsSearchInfo'
  );

  storageClient?.removeFromStorage('sessionStorage', 'analyticsSearchInfo');
  return parseJson(analyticsSearchInfo) || {};
};

const getMktTrackDimension = (): Pick<FieldsObject, 'dimension33'> => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();
  const dimension33 = storageClient?.getCookie(MKTTRACK_COOKIE_NAME);
  return { dimension33 };
};

const getReferralFields = (): Pick<
  FieldsObject,
  'campaignSource' | 'campaignMedium'
> => {
  const { getGoogleAnalyticsParameters, getTrackingStorageClient } =
    createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();
  const referral = extractDomain(document.referrer);
  const { selfReferrals } = getGoogleAnalyticsParameters();
  const selfReferralsArray = Array.isArray(selfReferrals)
    ? selfReferrals
    : [selfReferrals];
  const hasCrossDomainParameter = location.search.includes('_ga=');
  const hasActiveGaSession =
    storageClient?.getCookie('_gaActiveSession') === '1';

  if (
    selfReferralsArray.includes(referral) &&
    !hasCrossDomainParameter &&
    !hasActiveGaSession
  ) {
    return {
      campaignSource: 'referral',
      campaignMedium: referral,
    };
  }
  return {};
};

const getBundleIdFields = (): Pick<FieldsObject, 'dimension57'> => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();
  const bundleId = storageClient?.getFromStorage(
    'sessionStorage',
    'bookingInfo',
    null
  ) as BundleId | null;

  if (bundleId?.shoppingBasketBundle?.id) {
    return {
      dimension57: bundleId.shoppingBasketBundle.id,
    };
  }
  return {};
};

const getMobileFields = (): Pick<FieldsObject, 'dimension49'> => {
  const { getGenericParameters } = createConfigParametersRepository();
  const { flow } = getGenericParameters();
  if (flow === 'mobile') {
    return {
      dimension49:
        (window.navigator.connection && window.navigator.connection.type) ||
        'unknown',
    };
  }
  return {};
};

const configureFieldsObjectForPageview = async (
  options: TrackPageOptions
): Promise<Required<FieldsObject>> => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();

  const fieldObjectDimensions: FieldsObject = {
    ...getMktTrackDimension(),
    ...getBundleIdFields(),
    ...getMobileFields(),
  };

  storageClient?.updateCustomParamsAndDimensions(fieldObjectDimensions);
  gaWrapper('set', getCustomDimensionsFromParams(fieldObjectDimensions));

  return {
    ...fieldObjectDimensions,
    ...getReferralFields(),
    ...(await getSearchInfoCustomDimensions()),
    ...(options?.eCommerce?.customMetrics || {}),
  };
};

const getTrackingArgs = (
  brand: string,
  isNativeInterface: boolean,
  virtualPage: string
) => {
  return isNativeInterface
    ? createNativeTracking(brand, virtualPage)
    : createWebTracking(virtualPage);
};

const updateArgsWithFieldsObject = (
  args: any[],
  fieldsObject: FieldsObject
) => {
  if (Object.keys(fieldsObject).length !== 0) {
    const hitCallbackObject = args[3];
    const optionalFields = { ...hitCallbackObject, ...fieldsObject };
    args.splice(3, 0, optionalFields);
  }
  return args;
};

export const trackPostponedEvents = () => {
  const { getEventsQueue, emptyEventsQueue } = createRuntimeGoogleState();
  const eventsQueue = getEventsQueue();
  eventsQueue?.forEach((event) => trackEvent(event));
  emptyEventsQueue();
};

const trackGoogleAnalyticsPage = async (
  page: string,
  options?: TrackPageOptions
) => {
  const { customDimensions } = options ?? {};
  const { setParameters } = createRuntimeGoogleState();
  const { getGenericParameters } = createConfigParametersRepository();
  const { brand, isNativeInterface } = getGenericParameters();

  let args = getTrackingArgs(brand, isNativeInterface, page);

  const fieldsObject = await configureFieldsObjectForPageview(options);
  args = updateArgsWithFieldsObject(args, fieldsObject);

  const pageBeDimensions = await getPageCustomDimensions(page);
  await updatePageCustomDimensions({
    ...pageBeDimensions,
    ...customDimensions,
    screen_name: page,
    url: document.location.href,
    buypath: options?.funnel ? getBuyPathParam() : undefined,
  });
  trackPageInUniversal(args);
  trackPageInGa4(page);

  setParameters({ hasThePageBeenTracked: true });
  trackPostponedEvents();
};

export const trackPage = async (page: string, options?: TrackPageOptions) => {
  handlePageError(page);
  const { getGoogleState, setParameters } = createRuntimeGoogleState();
  const { lastTrackedPage } = getGoogleState();
  const { error } = options ?? {};
  if (lastTrackedPage === page || error) return;
  await checkGaInit();
  setParameters({ lastTrackedPage: page, hasThePageBeenTracked: false });
  await trackGoogleAnalyticsPage(page, options);
};
